/**
 * Fleey Portal API client.
 *
 * Deliberately a sibling of the console's client rather than a copy of it. The
 * portal signs in at a different endpoint, stores its session under a different
 * key, and has no permission model at all — a portal token carries one scope,
 * and every endpoint it can reach is already scoped to the customer holding it.
 */

const BASE = '/v1'

export interface ApiErrorDetail {
  field?: string
  code?: string
  message?: string
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: ApiErrorDetail[] = [],
    readonly requestId = '',
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Field-keyed messages, ready to drop into a form. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    for (const d of this.details) {
      if (d.field && d.message) out[d.field] = d.message
    }
    return out
  }
}

interface Tokens {
  access: string
  refresh: string
}

// Not 'fleey.session'. localStorage is keyed by origin, so if the portal is ever
// served from the console's origin, sharing a key would have each sign-in evict
// the other.
const STORAGE_KEY = 'fleey.portal.session'

export function loadTokens(): Tokens | null {
  const raw = localStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as Tokens
  } catch {
    return null
  }
}

export function saveTokens(tokens: Tokens): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens))
}

export function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEY)
}

let refreshInFlight: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  // Collapse concurrent 401s into one refresh; rotating twice trips the
  // server's reuse detection and ends the session outright.
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const tokens = loadTokens()
    if (!tokens?.refresh) return null

    const response = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: tokens.refresh }),
    })

    if (!response.ok) {
      clearTokens()
      return null
    }

    const body = await response.json()
    const next = {
      access: body.data.access_token as string,
      refresh: body.data.refresh_token as string,
    }
    saveTokens(next)
    return next.access
  })().finally(() => {
    refreshInFlight = null
  })

  return refreshInFlight
}

interface RequestOptions {
  method?: string
  body?: unknown
  idempotencyKey?: string
  signal?: AbortSignal
  retryOn401?: boolean
  /** Public tracking has no session; sending a stale token would only 401 it. */
  anonymous?: boolean
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    idempotencyKey,
    signal,
    retryOn401 = true,
    anonymous = false,
  } = options

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Fleey-App': 'portal',
    'X-Fleey-App-Version': '0.1.0',
  }

  const tokens = anonymous ? null : loadTokens()
  if (tokens?.access) headers.Authorization = `Bearer ${tokens.access}`
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey

  const response = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  })

  if (response.status === 401 && retryOn401 && !anonymous) {
    const fresh = await refreshAccessToken()
    if (fresh) return request<T>(path, { ...options, retryOn401: false })
  }

  if (response.status === 204) return undefined as T

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    const error = payload?.error ?? {}
    throw new ApiError(
      response.status,
      error.code ?? 'unknown_error',
      error.message ?? 'Something went wrong.',
      error.details ?? [],
      error.request_id ?? '',
    )
  }

  return payload as T
}

export interface Envelope<T> {
  data: T
  meta?: Record<string, unknown>
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<Envelope<T>>(path, { signal }),
  post: <T>(path: string, body?: unknown, idempotencyKey?: string) =>
    request<Envelope<T>>(path, { method: 'POST', body, idempotencyKey }),
  /** For the public tracking page, which is reached without signing in. */
  anonymous: <T>(path: string, signal?: AbortSignal) =>
    request<Envelope<T>>(path, { signal, anonymous: true }),
}

/** Stable key per logical action, so a retry cannot raise the same claim twice. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}
